import { z } from "zod";
import { norwegianBankAccountSchema, stoneHandlingDetailsSchema, stoneHandlingSchema, } from "./common.js";
/** The postal leg of a mail order — see MailLegStatus for why it has only three values. */
export const mailLegStatusSchema = z.enum([
    "REQUESTED",
    "RECEIVED_AT_STORE",
    "RETURNED_TO_CUSTOMER",
]);
const mailOrderShippingAddressSchema = z.object({
    line1: z.string().min(1).max(200),
    line2: z.string().max(200).nullable(),
    postalCode: z.string().regex(/^\d{4}$/, "Bruk fire siffer"),
    city: z.string().min(1).max(120),
    country: z.string().min(2).max(60).default("NO"),
});
const mailOrderItemInputSchema = z.object({
    description: z.string().min(1).max(200),
});
const mailOrderContentSchema = z.object({
    shippingAddress: mailOrderShippingAddressSchema,
    stoneHandling: stoneHandlingSchema,
    stoneHandlingDetails: stoneHandlingDetailsSchema.nullable().optional(),
    items: z.array(mailOrderItemInputSchema).min(1).max(50),
    customerComment: z.string().max(2000).nullable().optional().default(null),
    orderImageUrls: z.array(z.string().min(1).max(500)).max(20).optional().default([]),
    bankAccountNumber: norwegianBankAccountSchema.optional(),
    kontoEier: z.string().max(120).optional(),
    salesTermsAccepted: z.literal(true, {
        errorMap: () => ({ message: "Salgsbetingelsene må godtas" }),
    }),
});
const requireKontoEierWhenAccount = (value) => !value.bankAccountNumber || (value.kontoEier?.trim().length ?? 0) > 0;
export const createMailOrderSchema = mailOrderContentSchema.refine(requireKontoEierWhenAccount, {
    path: ["kontoEier"],
    message: "Eier av konto må fylles ut når kontonummer er satt",
});
/**
 * The postal leg, and only the postal leg. Weighing, settling and cancelling a mail order are not
 * postal events — they are the same operations a check-in has, and they live on the shared delivery
 * routes now. What is left is the two edges a check-in has no equivalent for: the pouch arriving,
 * and the gold going back.
 */
export const mailLegActionSchema = z.discriminatedUnion("action", [
    z.object({
        action: z.literal("mark-received"),
        /** The employee who took the pouch in. Required by the admin form, optional on the wire. */
        receivedByName: z.string().trim().max(80).optional(),
    }),
    z.object({
        action: z.literal("mark-returned"),
        overridePayment: z.boolean().optional(),
    }),
]);
export const createMailOrderResponseSchema = z.object({
    id: z.string(),
    receiptCode: z.string(),
});
export const mailLegActionResponseSchema = z.object({ status: mailLegStatusSchema });
